/**
 * Charge des tournées validées sur les 3 semaines à venir (#982), telle que la rend
 * `GET /logistics/tour-load-checks` : un contrôle par tournée et par jour de départ, recalculé
 * chaque nuit après la projection du réappro, avec les avertissements colorés de #975. Rouge : la
 * tournée ne tient plus ce jour-là, à redécouper avant la livraison ; orange : à vérifier. Rien ne
 * bloque une tournée validée. Ce module ne fait que regrouper et libeller ; sans JSX, testé par
 * `tour-load-checks.test.mjs`.
 */

import { formatInteger } from '@gozoki/tools'
import type { TourLoadCheck, TourLoadCheckVehicleLoad } from '~/lib/logistics-operations-query'
import { frenchWeekdayDayMonthOfIso } from '~/lib/iso-date'
import { pluralize } from '~/lib/utils'
import { type RunWarningLevel, STATUS_LABEL_TO_CHECK, worstRunWarningLevelOf } from './run-warnings'
import { capacityFillPercentOf, formatPayloadKg, vehicleFallbackLabel } from './scenario-view'

/** Statut d'une tournée ou d'un jour : le sens du rouge n'est pas celui d'une simulation (#975). */
export const TOUR_LOAD_LEVEL_LABELS: Record<RunWarningLevel, string> = {
  red: 'À redécouper',
  orange: STATUS_LABEL_TO_CHECK,
}

/** Pastille d'une tournée validée, lue par un lecteur d'écran : les mêmes, en minuscules. */
export const TOUR_LOAD_DOT_LABELS: Record<RunWarningLevel, string> = {
  red: TOUR_LOAD_LEVEL_LABELS.red.toLowerCase(),
  orange: TOUR_LOAD_LEVEL_LABELS.orange.toLowerCase(),
}

/** Tournée qui a au moins un jour rouge ou orange dans les 3 semaines. */
export interface TourLoadAlert {
  tour: TourLoadCheck['tour']
  level: RunWarningLevel
  /**
   * Jours de départ à ce niveau, dans l'ordre des dates : les rouges seuls pour une tournée à
   * redécouper (ses jours orange restent dans l'onglet Charge), sinon les orange.
   */
  days: TourLoadCheck[]
}

/** Niveau du plus grave des jours, `null` si aucun n'a d'avertissement. */
export const worstTourLoadLevelOf = (
  checks: readonly Pick<TourLoadCheck, 'level'>[],
): RunWarningLevel | null =>
  worstRunWarningLevelOf(
    checks.flatMap((check) => (check.level === null ? [] : [{ level: check.level }])),
  )

/**
 * Tournées en alerte : les rouges d'abord, puis celles dont le premier jour à leur niveau vient le
 * plus tôt, puis par nom, au numéro près (« T-999 » avant « T-1000 »). L'API trie déjà les
 * contrôles par date : l'ordre des jours en découle.
 */
export const tourLoadAlertsOf = (checks: readonly TourLoadCheck[]): TourLoadAlert[] => {
  const byTour = new Map<number, TourLoadAlert>()
  for (const check of checks) {
    if (check.level === null) continue
    const alert = byTour.get(check.tour.id)
    if (alert === undefined) {
      byTour.set(check.tour.id, { tour: check.tour, level: check.level, days: [check] })
    } else if (check.level === alert.level) {
      alert.days.push(check)
    } else if (check.level === 'red') {
      // Premier jour rouge d'une tournée jusque-là orange : seuls les rouges comptent désormais.
      alert.level = 'red'
      alert.days = [check]
    }
  }
  return [...byTour.values()].sort(
    (a, b) =>
      Number(b.level === 'red') - Number(a.level === 'red') ||
      a.days[0]!.departureDate.localeCompare(b.days[0]!.departureDate) ||
      a.tour.name.localeCompare(b.tour.name, 'fr', { numeric: true }),
  )
}

/** Jours de départ recalculés d'une tournée, dans l'ordre des dates. */
export const tourLoadChecksOf = (
  checks: readonly TourLoadCheck[],
  tourId: number,
): TourLoadCheck[] => checks.filter((check) => check.tour.id === tourId)

/** Niveau de chaque tournée en alerte, pour les lignes de la liste. */
export const tourLoadLevelsOf = (alerts: readonly TourLoadAlert[]): Map<number, RunWarningLevel> =>
  new Map(alerts.map((alert) => [alert.tour.id, alert.level]))

/** « À redécouper · 2 jours » : statut et nombre de jours en alerte. */
export const tourLoadAlertLabelOf = (alert: Pick<TourLoadAlert, 'level' | 'days'>): string =>
  `${TOUR_LOAD_LEVEL_LABELS[alert.level]} · ${pluralize(alert.days.length, 'jour')}`

/** « 2 tournées à redécouper, 1 à vérifier » ; `null` sans alerte. */
export const tourLoadSummaryOf = (
  alerts: readonly Pick<TourLoadAlert, 'level'>[],
): string | null => {
  const red = alerts.filter((alert) => alert.level === 'red').length
  const orange = alerts.length - red
  if (alerts.length === 0) return null
  if (red === 0) return `${pluralize(orange, 'tournée')} à vérifier`
  const toSplit = `${pluralize(red, 'tournée')} à redécouper`
  return orange === 0 ? toSplit : `${toSplit}, ${orange} à vérifier`
}

/** « lundi 02/11 » pour une date `YYYY-MM-DD` (jour civil, sans fuseau). */
export const formatLoadDay = frenchWeekdayDayMonthOfIso

/** Jour d'une ligne : le départ, et la livraison quand elle tombe le lendemain (tournée de nuit). */
export const loadDayLabelOf = (check: Pick<TourLoadCheck, 'departureDate' | 'deliveryDate'>) =>
  check.deliveryDate === check.departureDate
    ? formatLoadDay(check.departureDate)
    : `${formatLoadDay(check.departureDate)}, livrée le ${formatLoadDay(check.deliveryDate)}`

/** Camion d'une charge : son nom, ou son numéro s'il a été supprimé depuis le recalcul. */
export const loadVehicleLabelOf = (load: TourLoadCheckVehicleLoad): string =>
  load.vehicle?.label ?? `${vehicleFallbackLabel(load.vehicleId)} (supprimé)`

/** « 13 / 16 emplacements » : les emplacements de pali occupés sur ceux du camion. */
export const loadSlotsLabelOf = (load: TourLoadCheckVehicleLoad): string =>
  `${load.paliSlots} / ${pluralize(load.capacityPaliSlots, 'emplacement')}`

/** « 650 / 8 000 kg » ; le poids seul sans charge utile connue ; `null` sans poids (avant LU-160). */
export const loadWeightLabelOf = (load: TourLoadCheckVehicleLoad): string | null => {
  if (load.weightKg === null) return null
  return load.payloadKg === null
    ? formatPayloadKg(load.weightKg)
    : `${formatInteger(load.weightKg)} / ${formatPayloadKg(load.payloadKg)}`
}

/** Le camion dépasse ses emplacements ou sa charge utile ce jour-là. */
export const isLoadOverCapacity = (load: TourLoadCheckVehicleLoad): boolean =>
  load.paliSlots > load.capacityPaliSlots ||
  (load.weightKg !== null && load.payloadKg !== null && load.weightKg > load.payloadKg)

/** Remplissage de la jauge, en pourcentage des emplacements (même règle que l'onglet Contraintes). */
export const loadFillPercentOf = (load: TourLoadCheckVehicleLoad): number =>
  capacityFillPercentOf(load.paliSlots, load.capacityPaliSlots)

/**
 * Où mène la ligne d'une tournée en alerte dans le bandeau : l'onglet Charge de son détail si elle
 * est en vigueur ; sa simulation si elle ne l'est pas encore et que l'utilisateur peut la lire ;
 * nulle part sinon, ni tant que « en vigueur » n'est pas connu (`isActive` à `undefined`).
 */
export const tourLoadAlertTargetOf = (
  isActive: boolean | undefined,
  canReadSimulations: boolean,
): 'detail' | 'simulation' | null => {
  if (isActive === true) return 'detail'
  return isActive === false && canReadSimulations ? 'simulation' : null
}
